import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import html2canvas from 'html2canvas';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ImageUploader } from '../../components/ImageUploader';
import { MultiImageUploader } from '../../components/MultiImageUploader';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Plus, Edit, Trash2, Save, Star, Bell, ExternalLink, Tag, AlertTriangle, BarChart3, FileText, Download, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { isAdmin, adminToken, adminLogout } = useAdmin();
  const navigate = useNavigate();

  // ==== GLOBAL PLATFORM SCOPE (affects every tab) ====
  // 'all' = combined view, 'website' = website-only, 'app' = app-only
  const [adminPlatform, setAdminPlatform] = useState(() => {
    try { return localStorage.getItem('zindal_admin_platform') || 'all'; } catch { return 'all'; }
  });
  const platformParam = adminPlatform === 'all' ? '' : `?platform=${adminPlatform}`;
  const withPlatform = (url) => {
    if (adminPlatform === 'all') return url;
    return url.includes('?') ? `${url}&platform=${adminPlatform}` : `${url}?platform=${adminPlatform}`;
  };
  
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [bulkRequests, setBulkRequests] = useState([]);
  const [settings, setSettings] = useState(null);
  const [uiControls, setUIControls] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [editingReview, setEditingReview] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({ code: '', discountType: 'percentage', discountValue: '', minOrderAmount: '', maxUses: '', active: true, expiresAt: '', platform: 'both' });
  const [inventoryAlerts, setInventoryAlerts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [todaySales, setTodaySales] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [salesReport, setSalesReport] = useState(null);
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportYear, setReportYear] = useState(new Date().getFullYear());

  // ==== PUSH NOTIFICATIONS (App) ====
  const [pushTokens, setPushTokens] = useState([]);
  const [notifHistory, setNotifHistory] = useState([]);
  const [autoNotifSettings, setAutoNotifSettings] = useState({ placed: true, confirmed: true, shipped: true, delivered: true });
  const [pushForm, setPushForm] = useState({
    title: '', body: '', imageUrl: '',
    deepLinkType: 'home', deepLinkTarget: '',
    audience: 'all', scheduledFor: '',
  });
  const [sendingPush, setSendingPush] = useState(false);

  // ==== APP-SPECIFIC SETTINGS OVERRIDES ====
  const [appSettingsOverride, setAppSettingsOverride] = useState({});
  const [webSettingsOverride, setWebSettingsOverride] = useState({});
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditName, setInlineEditName] = useState('');
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', originalPrice: '', category: 'Festival Lighting', image: '', images: [], stock: 100, moq: 1, soldCount: 0, lowStockThreshold: 10, featured: false, seoTitle: '', seoDescription: '', seoKeywords: '', platform: 'both'
  });
  const [productPlatformFilter, setProductPlatformFilter] = useState('all'); // all | website | app | both

  useEffect(() => {
    if (!isAdmin) {
      navigate('/boss/login');
      return;
    }
    fetchAllData();
  }, [isAdmin, adminPlatform]);

  const axiosConfig = { headers: { Authorization: `Bearer ${adminToken}` } };

  const fetchAllData = async () => {
    try {
      const [productsRes, ordersRes, usersRes, bulkRes, settingsRes, uiRes, statsRes, reviewsRes, notifsRes, couponsRes, alertsRes, analyticsRes, todaySalesRes, invoicesRes, categoriesRes] = await Promise.all([
        axios.get(withPlatform(`${API}/products`)),
        axios.get(withPlatform(`${API}/orders`), axiosConfig),
        axios.get(`${API}/users`, axiosConfig),
        axios.get(`${API}/bulk-requests`, axiosConfig),
        axios.get(withPlatform(`${API}/settings`)),
        axios.get(withPlatform(`${API}/ui-controls`)),
        axios.get(`${API}/orders/stats/summary`, axiosConfig).catch(() => ({ data: null })),
        axios.get(`${API}/reviews`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/coupons`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/inventory/alerts`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/revenue`, axiosConfig).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/today-sales`, axiosConfig).catch(() => ({ data: null })),
        axios.get(`${API}/invoices`, axiosConfig).catch(() => ({ data: [] })),
        axios.get(withPlatform(`${API}/categories`)).catch(() => ({ data: [] }))
      ]);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setUsers(usersRes.data);
      setBulkRequests(bulkRes.data);
      setSettings(settingsRes.data);
      setUIControls(uiRes.data);
      if (statsRes.data) setOrderStats(statsRes.data);
      setReviews(reviewsRes.data);
      setNotifications(notifsRes.data);
      setCoupons(couponsRes.data);
      setInventoryAlerts(alertsRes.data);
      if (analyticsRes.data) setAnalytics(analyticsRes.data);
      if (todaySalesRes.data) setTodaySales(todaySalesRes.data);
      setInvoices(invoicesRes.data);
      setCategories(categoriesRes.data);

      // Push / notifications (App)
      try {
        const [tokensRes, historyRes, autoRes, appOverrideRes, webOverrideRes] = await Promise.all([
          axios.get(`${API}/push-tokens`, axiosConfig).catch(() => ({ data: [] })),
          axios.get(`${API}/notifications/history`, axiosConfig).catch(() => ({ data: [] })),
          axios.get(`${API}/notifications/auto-settings`, axiosConfig).catch(() => ({ data: { placed: true, confirmed: true, shipped: true, delivered: true } })),
          axios.get(`${API}/settings/platform/app`, axiosConfig).catch(() => ({ data: {} })),
          axios.get(`${API}/settings/platform/website`, axiosConfig).catch(() => ({ data: {} })),
        ]);
        setPushTokens(tokensRes.data || []);
        setNotifHistory(historyRes.data || []);
        setAutoNotifSettings(autoRes.data || { placed: true, confirmed: true, shipped: true, delivered: true });
        setAppSettingsOverride(appOverrideRes.data || {});
        setWebSettingsOverride(webOverrideRes.data || {});
      } catch (e) {
        // non-fatal
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        originalPrice: productForm.originalPrice ? parseFloat(productForm.originalPrice) : null,
        stock: parseInt(productForm.stock),
        moq: parseInt(productForm.moq) || 1,
        soldCount: parseInt(productForm.soldCount) || 0,
        lowStockThreshold: parseInt(productForm.lowStockThreshold) || 10
      };
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, data, axiosConfig);
        toast.success('Product updated!');
      } else {
        await axios.post(`${API}/products`, data, axiosConfig);
        toast.success('Product created!');
      }
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', originalPrice: '', category: 'Festival Lighting', image: '', images: [], stock: 100, moq: 1, soldCount: 0, lowStockThreshold: 10, featured: false, seoTitle: '', seoDescription: '', seoKeywords: '', platform: 'both' });
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleInlineNameSave = async (productId) => {
    const trimmed = inlineEditName.trim();
    if (!trimmed) { setInlineEditId(null); return; }
    try {
      await axios.put(`${API}/products/${productId}`, { name: trimmed }, axiosConfig);
      toast.success('Product name updated!');
      setProducts(prev => prev.map(p => p.id === productId ? { ...p, name: trimmed } : p));
    } catch (error) {
      toast.error('Failed to update name');
    }
    setInlineEditId(null);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name, description: product.description, price: product.price,
      originalPrice: product.originalPrice || '', category: product.category,
      image: product.image, images: product.images || [], stock: product.stock,
      moq: product.moq || 1, soldCount: product.soldCount || 0,
      lowStockThreshold: product.lowStockThreshold || 10, featured: product.featured,
      seoTitle: product.seoTitle || '', seoDescription: product.seoDescription || '', seoKeywords: product.seoKeywords || '',
      platform: product.platform || 'both'
    });
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, axiosConfig);
      toast.success('Product deleted');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}`, { status }, axiosConfig);
      toast.success('Order status updated');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleSaveSettings = async () => {
    try {
      if (adminPlatform === 'all') {
        await axios.put(`${API}/settings`, settings, axiosConfig);
        toast.success('Shared settings saved (applies to both Website & App)');
      } else {
        await axios.put(`${API}/settings/platform/${adminPlatform}`, settings, axiosConfig);
        toast.success(`${adminPlatform.toUpperCase()}-only settings saved`);
      }
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleSaveUIControls = async () => {
    try {
      if (adminPlatform === 'all') {
        await axios.put(`${API}/ui-controls`, uiControls, axiosConfig);
        toast.success('Shared UI Controls saved (applies to both Website & App)');
      } else {
        await axios.put(`${API}/ui-controls/platform/${adminPlatform}`, uiControls, axiosConfig);
        toast.success(`${adminPlatform.toUpperCase()}-only UI Controls saved`);
      }
    } catch (error) {
      toast.error('Failed to save UI controls');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}`, axiosConfig);
      toast.success('Review deleted');
      setReviews(prev => prev.filter(r => r.id !== reviewId));
    } catch (error) {
      toast.error('Failed to delete review');
    }
  };

  const handleUpdateReview = async (reviewId, data) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}`, data, axiosConfig);
      toast.success('Review updated');
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, ...data } : r));
      setEditingReview(null);
    } catch (error) {
      toast.error('Failed to update review');
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, axiosConfig);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      toast.error('Failed to mark notifications read');
    }
  };

  const fetchSalesReport = async (y, m) => {
    try {
      const res = await axios.get(`${API}/analytics/sales-report?year=${y}&month=${m}`, axiosConfig);
      setSalesReport(res.data);
    } catch { toast.error('Failed to fetch report'); }
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...couponForm,
        discountValue: parseFloat(couponForm.discountValue),
        minOrderAmount: parseFloat(couponForm.minOrderAmount) || 0,
        maxUses: parseInt(couponForm.maxUses) || 0,
        expiresAt: couponForm.expiresAt || null
      };
      await axios.post(`${API}/coupons`, data, axiosConfig);
      toast.success('Coupon created!');
      setShowCouponForm(false);
      setCouponForm({ code: '', discountType: 'percentage', discountValue: '', minOrderAmount: '', maxUses: '', active: true, expiresAt: '', platform: 'both' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create coupon');
    }
  };

  const handleDeleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await axios.delete(`${API}/coupons/${id}`, axiosConfig);
      toast.success('Coupon deleted');
      setCoupons(prev => prev.filter(c => c.id !== id));
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/boss/login');
  };

  // ==== PUSH NOTIFICATION HANDLERS ====
  const handleSendPush = async (e) => {
    e?.preventDefault();
    if (!pushForm.title.trim() || !pushForm.body.trim()) {
      toast.error('Title and body are required');
      return;
    }
    setSendingPush(true);
    try {
      const deepLink = pushForm.deepLinkTarget
        ? `${pushForm.deepLinkType}:${pushForm.deepLinkTarget}`
        : pushForm.deepLinkType;
      const payload = {
        title: pushForm.title.slice(0, 100),
        body: pushForm.body.slice(0, 240),
        imageUrl: pushForm.imageUrl || null,
        deepLink,
        audience: pushForm.audience,
        scheduledFor: pushForm.scheduledFor ? new Date(pushForm.scheduledFor).toISOString() : null,
      };
      const res = await axios.post(`${API}/notifications/send`, payload, axiosConfig);
      if (res.data.scheduled) toast.success(`Scheduled for ${new Date(res.data.scheduledFor).toLocaleString()}`);
      else toast.success(`Sent to ${res.data.sent} device(s)`);
      setPushForm({ title: '', body: '', imageUrl: '', deepLinkType: 'home', deepLinkTarget: '', audience: 'all', scheduledFor: '' });
      // Refresh history
      const h = await axios.get(`${API}/notifications/history`, axiosConfig).catch(() => ({ data: [] }));
      setNotifHistory(h.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send notification');
    }
    setSendingPush(false);
  };

  const handleResendPush = async (n) => {
    setPushForm({
      title: n.title || '', body: n.body || '', imageUrl: n.imageUrl || '',
      deepLinkType: (n.deepLink || 'home').split(':')[0] || 'home',
      deepLinkTarget: (n.deepLink || '').split(':').slice(1).join(':') || '',
      audience: n.audience || 'all', scheduledFor: '',
    });
    toast.info('Form prefilled — review & hit Send');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAutoNotifToggle = async (key) => {
    const next = { ...autoNotifSettings, [key]: !autoNotifSettings[key] };
    setAutoNotifSettings(next);
    try {
      await axios.put(`${API}/notifications/auto-settings`, next, axiosConfig);
      toast.success('Auto-notification settings saved');
    } catch {
      toast.error('Failed to save — reverting');
      setAutoNotifSettings(autoNotifSettings);
    }
  };

  const handleSavePlatformSettings = async (platform) => {
    try {
      const overrides = platform === 'app' ? appSettingsOverride : webSettingsOverride;
      await axios.put(`${API}/settings/platform/${platform}`, overrides, axiosConfig);
      toast.success(`${platform.toUpperCase()} settings saved`);
    } catch {
      toast.error('Failed to save');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="text-[#0A192F] hover:text-[#D4AF37] transition-colors flex items-center gap-1 text-sm font-medium" data-testid="admin-back-to-site">
              <ArrowLeft size={18} /> Back to Site
            </a>
            <h1 className="text-2xl font-bold" style={{fontFamily: 'Playfair Display, serif'}} data-testid="admin-header">ZINDAL Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            {notifications.filter(n => !n.read).length > 0 && (
              <span className="flex items-center gap-1 text-sm text-red-600 font-medium" data-testid="notification-badge">
                <Bell size={16} /> {notifications.filter(n => !n.read).length} new
              </span>
            )}
            <Button onClick={handleLogout} className="border border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white bg-transparent" data-testid="admin-logout-btn">
            <LogOut className="mr-2" size={18} />
            Logout
          </Button>
          </div>
        </div>

        {/* GLOBAL PLATFORM SWITCHER */}
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Viewing:</span>
            <div className="inline-flex bg-gray-100 p-1 rounded-full border border-gray-200" data-testid="platform-scope-switcher">
              {[
                { key: 'all', label: 'All', emoji: '🌐', color: 'bg-gray-900 text-white' },
                { key: 'website', label: 'Website', emoji: '💻', color: 'bg-blue-600 text-white' },
                { key: 'app', label: 'App', emoji: '📱', color: 'bg-purple-600 text-white' },
              ].map(opt => {
                const active = adminPlatform === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => {
                      setAdminPlatform(opt.key);
                      try { localStorage.setItem('zindal_admin_platform', opt.key); } catch {}
                    }}
                    className={`px-4 py-1.5 text-xs font-bold rounded-full transition ${active ? opt.color + ' shadow' : 'text-gray-600 hover:bg-white'}`}
                    data-testid={`scope-${opt.key}`}
                  >
                    {opt.emoji} {opt.label}
                  </button>
                );
              })}
            </div>
            <span className="text-xs text-gray-500">
              {adminPlatform === 'all' && 'All data from both Website and App'}
              {adminPlatform === 'website' && 'Showing only Website data — changes you make apply to Website'}
              {adminPlatform === 'app' && 'Showing only App data — changes you make apply to App'}
            </span>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#D4AF37]">{products.length}</p>
            <p className="text-xs text-gray-600 mt-1">Products</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{orderStats?.received || orders.length}</p>
            <p className="text-xs text-gray-600 mt-1">Received Orders</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{orderStats?.cancelled || 0}</p>
            <p className="text-xs text-gray-600 mt-1">Cancelled</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[#0A192F]">₹{(orderStats?.revenue || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-600 mt-1">Revenue</p>
          </div>
        </div>
        {/* Order status breakdown */}
        {orderStats && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-8">
            {[
              { label: 'Pending', val: orderStats.pending, color: 'text-gray-600' },
              { label: 'Confirmed', val: orderStats.confirmed, color: 'text-green-600' },
              { label: 'Processing', val: orderStats.processing, color: 'text-blue-600' },
              { label: 'Shipped', val: orderStats.shipped, color: 'text-purple-600' },
              { label: 'Delivered', val: orderStats.delivered, color: 'text-green-700' },
            ].map(s => (
              <div key={s.label} className="card p-3 text-center">
                <p className={`text-lg font-bold ${s.color}`}>{s.val}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="products" className="space-y-8">
          <TabsList className="w-full justify-start overflow-x-auto gap-1 bg-gray-50 p-1.5 rounded-lg">
            <TabsTrigger value="products" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="sales-report" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-sales-report">Sales Report</TabsTrigger>
            <TabsTrigger value="coupons" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-coupons">Coupons</TabsTrigger>
            <TabsTrigger value="inventory" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5 relative" data-testid="tab-inventory">
              Inventory
              {inventoryAlerts.length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[8px] font-bold bg-orange-500 text-white rounded-full">{inventoryAlerts.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="bulk" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-bulk">Bulk</TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5 relative" data-testid="tab-notifications">
              Alerts
              {notifications.filter(n => !n.read).length > 0 && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[8px] font-bold bg-red-500 text-white rounded-full">{notifications.filter(n => !n.read).length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-settings">Settings</TabsTrigger>
            <TabsTrigger value="ui" className="data-[state=active]:bg-[#0A192F] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-ui">UI Controls</TabsTrigger>
            <TabsTrigger value="push" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-push">📱 Push (App)</TabsTrigger>
            <TabsTrigger value="platform-settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white rounded-md text-xs font-medium px-3 py-1.5" data-testid="tab-platform-settings">App vs Web</TabsTrigger>
          </TabsList>

          {/* PRODUCTS TAB */}
          <TabsContent value="products">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Products ({products.filter(p => productPlatformFilter === 'all' || (p.platform || 'both') === productPlatformFilter).length})</h2>
              <Button onClick={() => {
                setShowProductForm(!showProductForm);
                setEditingProduct(null);
                const defaultPlatform = adminPlatform === 'all' ? 'both' : adminPlatform;
                setProductForm({ name: '', description: '', price: '', originalPrice: '', category: 'Festival Lighting', image: '', images: [], stock: 100, moq: 1, soldCount: 0, lowStockThreshold: 10, featured: false, seoTitle: '', seoDescription: '', seoKeywords: '', platform: defaultPlatform });
              }} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="add-product-btn">
                <Plus className="mr-2" size={18} /> Add Product
              </Button>
            </div>

            {/* Platform filter chips */}
            <div className="flex gap-2 mb-4 flex-wrap" data-testid="product-platform-filters">
              {[
                { key: 'all', label: 'All', count: products.length },
                { key: 'both', label: 'Both', count: products.filter(p => (p.platform || 'both') === 'both').length },
                { key: 'website', label: 'Website only', count: products.filter(p => p.platform === 'website').length },
                { key: 'app', label: 'App only', count: products.filter(p => p.platform === 'app').length },
              ].map(chip => (
                <button
                  key={chip.key}
                  onClick={() => setProductPlatformFilter(chip.key)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition ${productPlatformFilter === chip.key ? 'bg-[#0A192F] text-white border-[#0A192F]' : 'bg-white text-gray-700 border-gray-300 hover:border-[#0A192F]'}`}
                  data-testid={`platform-filter-${chip.key}`}
                >
                  {chip.label} · {chip.count}
                </button>
              ))}
            </div>

            {showProductForm && (
              <form onSubmit={handleProductSubmit} className="card p-6 mb-6" data-testid="product-form">
                <h3 className="text-lg font-bold mb-4">{editingProduct ? 'Edit Product' : 'New Product'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} required data-testid="product-name-input" />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <select value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                      className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      data-testid="product-category-select">
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Price *</Label>
                    <Input type="number" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} required data-testid="product-price-input" />
                  </div>
                  <div>
                    <Label>Original Price</Label>
                    <Input type="number" value={productForm.originalPrice} onChange={(e) => setProductForm({...productForm, originalPrice: e.target.value})} data-testid="product-original-price-input" />
                  </div>
                  <div>
                    <Label>Stock *</Label>
                    <Input type="number" value={productForm.stock} onChange={(e) => setProductForm({...productForm, stock: e.target.value})} required data-testid="product-stock-input" />
                  </div>
                  <div>
                    <Label>MOQ (Min Order Qty)</Label>
                    <Input type="number" value={productForm.moq} onChange={(e) => setProductForm({...productForm, moq: e.target.value})} min="1" data-testid="product-moq-input" />
                  </div>
                  <div>
                    <Label>Sold Count</Label>
                    <Input type="number" value={productForm.soldCount} onChange={(e) => setProductForm({...productForm, soldCount: e.target.value})} min="0" data-testid="product-sold-count-input" />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <input type="checkbox" id="featured" checked={productForm.featured} onChange={(e) => setProductForm({...productForm, featured: e.target.checked})} data-testid="product-featured-checkbox" />
                    <Label htmlFor="featured">Featured Product</Label>
                  </div>
                  <div>
                    <Label>Visible On *</Label>
                    <select value={productForm.platform} onChange={(e) => setProductForm({...productForm, platform: e.target.value})}
                      className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      data-testid="product-platform-select">
                      <option value="both">Both (Website + App)</option>
                      <option value="website">Website only</option>
                      <option value="app">App only</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Description *</Label>
                    <textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} required rows={3}
                      className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                      data-testid="product-description-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Product Image *</Label>
                    <div className="mt-1">
                      <ImageUploader
                        value={productForm.image}
                        onChange={(url) => setProductForm({...productForm, image: url})}
                        testId="product-image"
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Additional Gallery Images (drag & drop)</Label>
                    <div className="mt-1">
                      <MultiImageUploader
                        images={productForm.images}
                        onChange={(imgs) => setProductForm({...productForm, images: imgs})}
                        testId="product-gallery"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Low Stock Threshold</Label>
                    <Input type="number" value={productForm.lowStockThreshold} onChange={(e) => setProductForm({...productForm, lowStockThreshold: e.target.value})} min="0" data-testid="product-low-stock-input" />
                  </div>
                  <div>
                    <Label>SEO Title</Label>
                    <Input value={productForm.seoTitle} onChange={(e) => setProductForm({...productForm, seoTitle: e.target.value})} placeholder="SEO optimized title" data-testid="product-seo-title" />
                  </div>
                  <div>
                    <Label>SEO Description</Label>
                    <Input value={productForm.seoDescription} onChange={(e) => setProductForm({...productForm, seoDescription: e.target.value})} placeholder="Meta description" data-testid="product-seo-desc" />
                  </div>
                  <div>
                    <Label>SEO Keywords</Label>
                    <Input value={productForm.seoKeywords} onChange={(e) => setProductForm({...productForm, seoKeywords: e.target.value})} placeholder="keyword1, keyword2" data-testid="product-seo-keywords" />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button type="submit" className="bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="product-save-btn">Save Product</Button>
                  <Button type="button" onClick={() => { setShowProductForm(false); setEditingProduct(null); }} variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white">Cancel</Button>
                </div>
              </form>
            )}

            <div className="space-y-4">
              {products.filter(p => productPlatformFilter === 'all' || (p.platform || 'both') === productPlatformFilter).map(product => (
                <div key={product.id} className="card p-4 flex flex-col sm:flex-row gap-4" data-testid={`admin-product-${product.id}`}>
                  <img src={product.image} alt={product.name} className="w-24 h-24 object-cover flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        {inlineEditId === product.id ? (
                          <input
                            autoFocus
                            value={inlineEditName}
                            onChange={(e) => setInlineEditName(e.target.value)}
                            onBlur={() => handleInlineNameSave(product.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleInlineNameSave(product.id); if (e.key === 'Escape') setInlineEditId(null); }}
                            className="font-bold border-b-2 border-[#D4AF37] bg-transparent outline-none px-1 py-0.5 w-full max-w-xs"
                            data-testid={`inline-edit-name-${product.id}`}
                          />
                        ) : (
                          <h3
                            className="font-bold cursor-pointer hover:text-[#D4AF37] transition-colors"
                            onClick={() => { setInlineEditId(product.id); setInlineEditName(product.name); }}
                            title="Click to rename"
                            data-testid={`product-name-${product.id}`}
                          >{product.name}</h3>
                        )}
                        <p className="text-sm text-gray-600">{product.category}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {product.featured && <span className="bg-[#D4AF37] text-white text-xs px-2 py-1">Featured</span>}
                        {(() => {
                          const p = product.platform || 'both';
                          const styles = {
                            app: 'bg-purple-100 text-purple-700 border border-purple-200',
                            website: 'bg-blue-100 text-blue-700 border border-blue-200',
                            both: 'bg-green-100 text-green-700 border border-green-200',
                          };
                          const labels = { app: 'APP ONLY', website: 'WEB ONLY', both: 'BOTH' };
                          return <span className={`text-[10px] font-bold px-2 py-1 rounded ${styles[p]}`} data-testid={`platform-badge-${product.id}`}>{labels[p]}</span>;
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <p className="text-[#D4AF37] font-bold">₹{product.price}</p>
                      <p className="text-sm text-gray-600">Stock: {product.stock}</p>
                      <p className="text-sm text-gray-500">Sold: {product.soldCount || 0}</p>
                      {product.moq > 1 && <p className="text-sm text-amber-600">MOQ: {product.moq}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 self-start">
                    <Button onClick={() => handleEditProduct(product)} variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-3 py-2" data-testid={`edit-product-${product.id}`}><Edit size={16} /></Button>
                    <Button onClick={() => handleDeleteProduct(product.id)} className="bg-red-500 text-white hover:bg-red-600 px-3 py-2" data-testid={`delete-product-${product.id}`}><Trash2 size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ORDERS TAB */}
          <TabsContent value="orders">
            {/* Order summary stats */}
            {orderStats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                <div className="card p-4 border-l-4 border-green-500">
                  <p className="text-2xl font-bold text-green-600">{orderStats.received}</p>
                  <p className="text-xs text-gray-600">Received Orders</p>
                </div>
                <div className="card p-4 border-l-4 border-red-500">
                  <p className="text-2xl font-bold text-red-500">{orderStats.cancelled}</p>
                  <p className="text-xs text-gray-600">Cancelled Orders</p>
                </div>
                <div className="card p-4 border-l-4 border-[#D4AF37]">
                  <p className="text-2xl font-bold text-[#D4AF37]">₹{(orderStats.revenue || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-600">Total Revenue</p>
                </div>
              </div>
            )}
            <h2 className="text-2xl font-bold mb-6">All Orders ({orders.length})</h2>
            {orders.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No orders yet</div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => {
                  const op = order.platform || 'both';
                  const opStyles = { app: 'bg-purple-100 text-purple-700 border-purple-200', website: 'bg-blue-100 text-blue-700 border-blue-200', both: 'bg-green-100 text-green-700 border-green-200' };
                  const opLabels = { app: '📱 APP', website: '💻 WEB', both: '🌐 BOTH' };
                  return (
                  <div key={order.id} className="card p-6" data-testid={`admin-order-${order.id}`}>
                    <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-lg">{order.orderId}</h3>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${opStyles[op]}`} data-testid={`order-platform-${order.id}`}>{opLabels[op]}</span>
                        </div>
                        <p className="text-sm text-gray-600">{order.customerName} | {order.customerPhone}</p>
                        <p className="text-sm text-gray-600">{order.address}</p>
                        <p className="text-xs text-gray-500 mt-1">{new Date(order.createdAt).toLocaleString()}</p>
                      </div>
                      <div className="text-right flex flex-col items-end gap-2">
                        <p className="text-[#D4AF37] font-bold text-xl">₹{order.total}</p>
                        <select
                          value={order.status}
                          onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                          className="px-3 py-1 border border-gray-300 text-sm bg-white"
                          data-testid={`order-status-select-${order.id}`}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="processing">Processing</option>
                          <option value="shipped">Shipped</option>
                          <option value="delivered">Delivered</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>
                    <div className="border-t pt-3 flex flex-wrap gap-3">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-gray-50 px-3 py-2 text-sm">
                          <img src={item.image} alt={item.name} className="w-8 h-8 object-cover" />
                          <span>{item.name} x{item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* INVOICES TAB */}
          <TabsContent value="invoices">
            <h2 className="text-2xl font-bold mb-6">Invoices ({invoices.length})</h2>

            {/* Invoice Detail Modal */}
            {selectedInvoice && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedInvoice(null)}>
                <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 rounded-lg" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal" id="invoice-printable">
                  {/* Invoice Header */}
                  <div className="flex justify-between items-start mb-8 border-b pb-6">
                    <div>
                      <h2 className="text-2xl font-bold" style={{fontFamily: 'Playfair Display, serif'}}>ZINDAL</h2>
                      <p className="text-sm text-gray-500">Premium Lighting Solutions</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-[#D4AF37]">INVOICE</p>
                      <p className="text-sm font-mono">{selectedInvoice.invoiceNumber}</p>
                      <p className="text-xs text-gray-500">{new Date(selectedInvoice.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="grid grid-cols-2 gap-6 mb-8">
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Bill To</p>
                      <p className="font-bold">{selectedInvoice.customerName}</p>
                      <p className="text-sm text-gray-600">{selectedInvoice.customerPhone}</p>
                      {selectedInvoice.customerEmail && <p className="text-sm text-gray-600">{selectedInvoice.customerEmail}</p>}
                      <p className="text-sm text-gray-600 mt-1">{selectedInvoice.address}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase mb-1">Order</p>
                      <p className="font-bold">{selectedInvoice.orderId}</p>
                      <p className="text-sm text-gray-600">Payment: {selectedInvoice.paymentMethod === 'cod' ? 'Cash on Delivery' : selectedInvoice.paymentMethod === 'upi' ? 'UPI' : 'Online'}</p>
                      {selectedInvoice.paymentId && selectedInvoice.paymentId !== 'COD' && <p className="text-xs text-gray-500">ID: {selectedInvoice.paymentId}</p>}
                    </div>
                  </div>

                  {/* Items Table */}
                  <table className="w-full mb-6">
                    <thead>
                      <tr className="border-b-2 border-[#D4AF37]">
                        <th className="text-left py-2 text-sm">Product</th>
                        <th className="text-center py-2 text-sm">Qty</th>
                        <th className="text-right py-2 text-sm">Price</th>
                        <th className="text-right py-2 text-sm">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedInvoice.items.map((item, idx) => (
                        <tr key={idx} className="border-b">
                          <td className="py-3">
                            <div className="flex items-center gap-3">
                              {item.image && <img src={item.image} alt={item.name} className="w-10 h-10 object-cover rounded" />}
                              <span className="text-sm font-medium">{item.name}</span>
                            </div>
                          </td>
                          <td className="text-center text-sm">{item.quantity}</td>
                          <td className="text-right text-sm">₹{item.price}</td>
                          <td className="text-right text-sm font-medium">₹{(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="border-t-2 border-gray-200 pt-4 space-y-2 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>₹{selectedInvoice.subtotal.toFixed(2)}</span></div>
                    {selectedInvoice.discount > 0 && <div className="flex justify-between text-green-600"><span>Discount</span><span>-₹{selectedInvoice.discount.toFixed(2)}</span></div>}
                    {selectedInvoice.couponDiscount > 0 && <div className="flex justify-between text-green-600"><span>Coupon Discount</span><span>-₹{selectedInvoice.couponDiscount.toFixed(2)}</span></div>}
                    <div className="flex justify-between"><span>GST ({selectedInvoice.gstRate}%)</span><span>₹{selectedInvoice.gst.toFixed(2)}</span></div>
                    <div className="flex justify-between"><span>Shipping</span><span>{selectedInvoice.shipping === 0 ? 'FREE' : `₹${selectedInvoice.shipping.toFixed(2)}`}</span></div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2 mt-2">
                      <span>Total</span>
                      <span className="text-[#D4AF37]">₹{selectedInvoice.total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    <Button onClick={async () => {
                      const el = document.getElementById('invoice-printable');
                      try {
                        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
                        const dataUrl = canvas.toDataURL('image/png');
                        // Try native share first (works on mobile)
                        if (navigator.share && navigator.canShare) {
                          const blob = await (await fetch(dataUrl)).blob();
                          const file = new File([blob], `${selectedInvoice.invoiceNumber}.png`, { type: 'image/png' });
                          if (navigator.canShare({ files: [file] })) {
                            await navigator.share({ files: [file], title: selectedInvoice.invoiceNumber });
                            toast.success('Shared!');
                            return;
                          }
                        }
                        // Fallback: direct download
                        const link = document.createElement('a');
                        link.download = `${selectedInvoice.invoiceNumber}.png`;
                        link.href = dataUrl;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        toast.success('Image saved!');
                      } catch (e) { toast.error('Failed: ' + e.message); }
                    }} className="bg-[#0A192F] text-white hover:bg-[#0A192F]/80 text-xs" data-testid="save-image-btn">
                      <Download size={14} className="mr-1" /> Save / Share Image
                    </Button>
                    <Button onClick={async () => {
                      const el = document.getElementById('invoice-printable');
                      try {
                        const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
                        canvas.toBlob((blob) => {
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${selectedInvoice.invoiceNumber}.png`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          setTimeout(() => URL.revokeObjectURL(url), 1000);
                          toast.success('File downloaded!');
                        }, 'image/png');
                      } catch (e) { toast.error('Failed: ' + e.message); }
                    }} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] text-xs" data-testid="download-invoice-btn">
                      <Download size={14} className="mr-1" /> Download File
                    </Button>
                    <Button onClick={() => {
                      const el = document.getElementById('invoice-printable');
                      const w = window.open('','_blank');
                      w.document.write(`<html><head><title>${selectedInvoice.invoiceNumber}</title><style>body{font-family:system-ui,sans-serif;padding:40px;color:#333;max-width:700px;margin:auto}table{width:100%;border-collapse:collapse}th,td{padding:10px 8px;text-align:left;border-bottom:1px solid #eee}th{font-size:12px;text-transform:uppercase;color:#666;border-bottom:2px solid #D4AF37}img{width:40px;height:40px;object-fit:cover;border-radius:4px}</style></head><body>${el.innerHTML}</body></html>`);
                      w.document.close(); setTimeout(() => w.print(), 500);
                    }} variant="outline" className="border-[#0A192F] text-[#0A192F] text-xs" data-testid="print-invoice-btn">
                      Print
                    </Button>
                    <Button onClick={() => setSelectedInvoice(null)} variant="outline" className="border-gray-300 text-gray-600 text-xs">Close</Button>
                    <a href={`${API}/invoices/${selectedInvoice.id}/html`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] text-xs">Open in Browser</Button>
                    </a>
                  </div>
                </div>
              </div>
            )}

            {invoices.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No invoices yet. Invoices are generated automatically when orders are placed.</div>
            ) : (
              <div className="space-y-3">
                {invoices.map(inv => {
                  const ip = inv.platform || 'both';
                  const ipStyles = { app: 'bg-purple-100 text-purple-700', website: 'bg-blue-100 text-blue-700', both: 'bg-green-100 text-green-700' };
                  const ipLabels = { app: '📱 APP', website: '💻 WEB', both: '🌐 BOTH' };
                  return (
                  <div key={inv.id} className="card p-4 flex items-center justify-between hover:border-[#D4AF37] transition-colors cursor-pointer" onClick={() => setSelectedInvoice(inv)} data-testid={`invoice-${inv.id}`}>
                    <div className="flex items-center gap-4">
                      <FileText size={20} className="text-[#D4AF37]" />
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-sm">{inv.invoiceNumber}</p>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ipStyles[ip]}`}>{ipLabels[ip]}</span>
                        </div>
                        <p className="text-xs text-gray-500">{inv.orderId} | {inv.customerName} | {inv.customerPhone}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#D4AF37]">₹{inv.total.toFixed(2)}</p>
                      <p className="text-[10px] text-gray-400">{new Date(inv.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <h2 className="text-2xl font-bold mb-6">Revenue Analytics</h2>
            {analytics ? (
              <div className="space-y-6">
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="card p-4 text-center border-l-4 border-[#D4AF37]">
                    <p className="text-2xl font-bold text-[#D4AF37]">₹{(analytics.totalRevenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">Total Revenue</p>
                  </div>
                  <div className="card p-4 text-center border-l-4 border-blue-500">
                    <p className="text-2xl font-bold text-blue-600">{analytics.totalOrders}</p>
                    <p className="text-xs text-gray-600 mt-1">Total Orders</p>
                  </div>
                  <div className="card p-4 text-center border-l-4 border-green-500">
                    <p className="text-2xl font-bold text-green-600">₹{(analytics.todayRevenue || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-600 mt-1">Today's Revenue</p>
                  </div>
                  <div className="card p-4 text-center border-l-4 border-purple-500">
                    <p className="text-2xl font-bold text-purple-600">{analytics.todayOrders}</p>
                    <p className="text-xs text-gray-600 mt-1">Today's Orders</p>
                  </div>
                </div>

                {/* Daily revenue chart */}
                {analytics.daily.length > 0 && (
                  <div className="card p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-bold text-lg">Daily Revenue</h3>
                      <span className="text-xs text-gray-400">Last 30 days</span>
                    </div>
                    {(() => {
                      const maxRev = Math.max(...analytics.daily.map(x => x.revenue)) || 1;
                      const gridLines = [0, 0.25, 0.5, 0.75, 1].map(f => Math.round(maxRev * f));
                      return (
                        <div className="relative" data-testid="daily-revenue-chart">
                          {/* Y-axis grid */}
                          <div className="absolute left-0 top-0 h-[200px] w-full">
                            {gridLines.reverse().map((val, i) => (
                              <div key={i} className="absolute w-full flex items-center" style={{ top: `${i * 25}%` }}>
                                <span className="text-[9px] text-gray-400 w-14 text-right pr-2">₹{val.toLocaleString()}</span>
                                <div className="flex-1 border-t border-gray-100"></div>
                              </div>
                            ))}
                          </div>
                          {/* Bars */}
                          <div className="flex items-end gap-2 h-[200px] pl-16 overflow-x-auto pb-8 pt-2">
                            {analytics.daily.map((d, i) => {
                              const height = (d.revenue / maxRev) * 100;
                              return (
                                <div key={i} className="flex flex-col items-center min-w-[40px] group relative">
                                  {/* Tooltip */}
                                  <div className="absolute -top-12 left-1/2 -translate-x-1/2 bg-[#0A192F] text-white text-[9px] px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                    <p className="font-bold">₹{d.revenue.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                                    <p className="text-gray-300">{d.orders} order{d.orders !== 1 ? 's' : ''}</p>
                                  </div>
                                  {/* Value on top */}
                                  <span className="text-[8px] text-[#D4AF37] font-bold mb-1">₹{d.revenue >= 1000 ? `${(d.revenue/1000).toFixed(1)}k` : Math.round(d.revenue)}</span>
                                  {/* Bar */}
                                  <div className="bg-gradient-to-t from-[#D4AF37] to-[#F5D77A] hover:from-[#c9a22e] hover:to-[#D4AF37] transition-all rounded-t w-8 cursor-pointer" style={{ height: `${Math.max(height, 3)}%` }} />
                                  {/* Date label */}
                                  <span className="text-[9px] text-gray-500 mt-2 font-medium">{d.date.slice(5)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Top products */}
                {analytics.topProducts.length > 0 && (
                  <div className="card p-6">
                    <h3 className="font-bold text-lg mb-4">Top Products</h3>
                    <div className="space-y-3">
                      {analytics.topProducts.map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 flex items-center justify-center bg-[#D4AF37]/10 text-[#D4AF37] font-bold text-xs rounded-full">{i + 1}</span>
                            <span className="font-medium text-sm">{p.name}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-[#D4AF37]">₹{p.revenue.toLocaleString()}</p>
                            <p className="text-[10px] text-gray-500">{p.sold} sold</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Today's Sales */}
                {todaySales && todaySales.products.length > 0 && (
                  <div className="card p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-lg">Today's Sales</h3>
                      <span className="bg-[#D4AF37] text-white text-xs px-2.5 py-1 rounded-full font-bold">{todaySales.totalUnitsSold} units sold</span>
                    </div>
                    <div className="space-y-3">
                      {todaySales.products.map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-3">
                            {p.image && <img src={p.image} alt="" className="w-10 h-10 rounded object-cover" />}
                            <div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-[10px] text-gray-500">{p.quantity} unit{p.quantity !== 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <span className="font-bold text-sm text-[#D4AF37]">₹{p.revenue.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bestsellers */}
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">Bestsellers</h3>
                    <span className="text-xs text-gray-400">Auto-detected + manual</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Top sellers are auto-detected from sales. Toggle manually to override.</p>
                  <div className="space-y-2">
                    {products.map(p => {
                      const isBs = p.bestseller || (p.soldCount > 0 && products.filter(x => x.soldCount > 0).sort((a, b) => b.soldCount - a.soldCount).slice(0, Math.max(1, Math.ceil(products.filter(x => x.soldCount > 0).length / 4))).some(x => x.id === p.id));
                      return (
                        <div key={p.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-3">
                            <img src={p.image} alt="" className="w-8 h-8 rounded object-cover" />
                            <div>
                              <p className="font-medium text-sm">{p.name}</p>
                              <p className="text-[10px] text-gray-500">{p.soldCount || 0} sold</p>
                            </div>
                          </div>
                          <button
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/products/${p.id}`, { bestseller: !p.bestseller }, axiosConfig);
                                toast.success(p.bestseller ? 'Removed from bestsellers' : 'Marked as bestseller');
                                fetchAllData();
                              } catch { toast.error('Failed'); }
                            }}
                            className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                              isBs ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                            data-testid={`bestseller-toggle-${p.id}`}
                          >
                            {isBs ? 'Bestseller' : 'Set Bestseller'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="card p-12 text-center text-gray-500">No analytics data yet. Place some orders first.</div>
            )}
          </TabsContent>

          {/* SALES REPORT TAB */}
          <TabsContent value="sales-report">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-2xl font-bold">Sales Report</h2>
              <div className="flex items-center gap-3">
                <select value={reportMonth} onChange={(e) => setReportMonth(parseInt(e.target.value))} className="h-9 px-3 border border-gray-300 rounded-md text-sm" data-testid="report-month">
                  {Array.from({length: 12}, (_, i) => <option key={i+1} value={i+1}>{new Date(2000, i).toLocaleString('default', {month: 'long'})}</option>)}
                </select>
                <select value={reportYear} onChange={(e) => setReportYear(parseInt(e.target.value))} className="h-9 px-3 border border-gray-300 rounded-md text-sm" data-testid="report-year">
                  {Array.from({length: 5}, (_, i) => { const y = new Date().getFullYear() - i; return <option key={y} value={y}>{y}</option>; })}
                </select>
                <Button onClick={() => fetchSalesReport(reportYear, reportMonth)} className="bg-[#0A192F] text-white hover:bg-[#0A192F]/80 h-9 px-4 text-xs" data-testid="fetch-report-btn">Load Report</Button>
                {salesReport && (
                  <a href={`${API}/analytics/sales-report/csv?year=${reportYear}&month=${reportMonth}`} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      fetch(`${API}/analytics/sales-report/csv?year=${reportYear}&month=${reportMonth}`, { headers: { 'Authorization': `Bearer ${adminToken}` } })
                        .then(r => r.blob()).then(blob => {
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url; a.download = `ZINDAL_Sales_${reportYear}_${String(reportMonth).padStart(2,'0')}.csv`;
                          document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                          toast.success('CSV downloaded!');
                        });
                    }}>
                    <Button className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] h-9 px-4 text-xs" data-testid="download-csv-btn">
                      <Download size={14} className="mr-1" /> Download CSV
                    </Button>
                  </a>
                )}
              </div>
            </div>

            {!salesReport ? (
              <div className="card p-12 text-center text-gray-500">Select month/year and click "Load Report"</div>
            ) : salesReport.rows.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No sales data for {new Date(2000, reportMonth - 1).toLocaleString('default', {month: 'long'})} {reportYear}</div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="card p-4 text-center border-l-4 border-[#D4AF37]">
                    <p className="text-2xl font-bold text-[#D4AF37]">₹{salesReport.totalRevenue.toLocaleString()}</p>
                    <p className="text-xs text-gray-600">Revenue</p>
                  </div>
                  <div className="card p-4 text-center border-l-4 border-blue-500">
                    <p className="text-2xl font-bold text-blue-600">{salesReport.totalOrders}</p>
                    <p className="text-xs text-gray-600">Orders</p>
                  </div>
                  <div className="card p-4 text-center border-l-4 border-green-500">
                    <p className="text-2xl font-bold text-green-600">{salesReport.totalUnits}</p>
                    <p className="text-xs text-gray-600">Units Sold</p>
                  </div>
                </div>

                {/* Daily breakdown */}
                <div className="card overflow-x-auto mb-6">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-xs font-medium">Date</th>
                        <th className="text-center p-3 text-xs font-medium">Orders</th>
                        <th className="text-center p-3 text-xs font-medium">Units</th>
                        <th className="text-right p-3 text-xs font-medium">Revenue</th>
                        <th className="text-left p-3 text-xs font-medium">Products Sold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesReport.dailySummary.map((d, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium text-sm">{d.date}</td>
                          <td className="p-3 text-center text-sm">{d.totalOrders}</td>
                          <td className="p-3 text-center text-sm font-bold">{d.totalUnits}</td>
                          <td className="p-3 text-right text-sm font-bold text-[#D4AF37]">₹{d.totalRevenue.toLocaleString()}</td>
                          <td className="p-3 text-sm text-gray-600">{Object.entries(d.products).map(([name, qty]) => `${name} x${qty}`).join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Detailed rows */}
                <div className="card overflow-x-auto">
                  <h3 className="font-bold p-4 border-b">Order Details</h3>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 text-xs font-medium">Date</th>
                        <th className="text-left p-3 text-xs font-medium">Order ID</th>
                        <th className="text-left p-3 text-xs font-medium">Customer</th>
                        <th className="text-left p-3 text-xs font-medium">Product</th>
                        <th className="text-center p-3 text-xs font-medium">Qty</th>
                        <th className="text-right p-3 text-xs font-medium">Price</th>
                        <th className="text-right p-3 text-xs font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesReport.rows.map((r, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50 text-sm">
                          <td className="p-3">{r.date}</td>
                          <td className="p-3 font-mono text-xs">{r.orderId}</td>
                          <td className="p-3">{r.customer}</td>
                          <td className="p-3 font-medium">{r.product}</td>
                          <td className="p-3 text-center">{r.quantity}</td>
                          <td className="p-3 text-right">₹{r.price}</td>
                          <td className="p-3 text-right font-bold">₹{r.lineTotal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </TabsContent>

          {/* COUPONS TAB */}
          <TabsContent value="coupons">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Coupons ({coupons.length})</h2>
              <Button onClick={() => setShowCouponForm(!showCouponForm)} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="add-coupon-btn">
                <Plus className="mr-2" size={18} /> Add Coupon
              </Button>
            </div>

            {showCouponForm && (
              <form onSubmit={handleCouponSubmit} className="card p-6 mb-6" data-testid="coupon-form">
                <h3 className="text-lg font-bold mb-4">New Coupon</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Coupon Code *</Label>
                    <Input value={couponForm.code} onChange={(e) => setCouponForm({...couponForm, code: e.target.value.toUpperCase()})} required placeholder="e.g. SAVE20" data-testid="coupon-code-input" />
                  </div>
                  <div>
                    <Label>Discount Type</Label>
                    <select value={couponForm.discountType} onChange={(e) => setCouponForm({...couponForm, discountType: e.target.value})}
                      className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm" data-testid="coupon-type-select">
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount (₹)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Discount Value *</Label>
                    <Input type="number" step="0.01" value={couponForm.discountValue} onChange={(e) => setCouponForm({...couponForm, discountValue: e.target.value})} required placeholder={couponForm.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 100'} data-testid="coupon-value-input" />
                  </div>
                  <div>
                    <Label>Min Order Amount (₹)</Label>
                    <Input type="number" step="0.01" value={couponForm.minOrderAmount} onChange={(e) => setCouponForm({...couponForm, minOrderAmount: e.target.value})} placeholder="0 = no minimum" data-testid="coupon-min-order-input" />
                  </div>
                  <div>
                    <Label>Max Uses (0 = unlimited)</Label>
                    <Input type="number" value={couponForm.maxUses} onChange={(e) => setCouponForm({...couponForm, maxUses: e.target.value})} placeholder="0" data-testid="coupon-max-uses-input" />
                  </div>
                  <div>
                    <Label>Expires At</Label>
                    <Input type="datetime-local" value={couponForm.expiresAt} onChange={(e) => setCouponForm({...couponForm, expiresAt: e.target.value})} data-testid="coupon-expiry-input" />
                  </div>
                  <div>
                    <Label>Available On *</Label>
                    <select value={couponForm.platform} onChange={(e) => setCouponForm({...couponForm, platform: e.target.value})}
                      className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm" data-testid="coupon-platform-select">
                      <option value="both">Both (Website + App)</option>
                      <option value="website">Website only</option>
                      <option value="app">App only (exclusive)</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button type="submit" className="bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="coupon-save-btn">Create Coupon</Button>
                  <Button type="button" onClick={() => setShowCouponForm(false)} variant="outline" className="border-[#0A192F] text-[#0A192F]">Cancel</Button>
                </div>
              </form>
            )}

            {coupons.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No coupons yet</div>
            ) : (
              <div className="space-y-3">
                {coupons.map(c => (
                  <div key={c.id} className="card p-4 flex items-center justify-between" data-testid={`coupon-${c.id}`}>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Tag size={16} className="text-[#D4AF37]" />
                        <span className="font-bold text-lg tracking-wider">{c.code}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{c.active ? 'Active' : 'Inactive'}</span>
                        {(() => {
                          const p = c.platform || 'both';
                          const styles = { app: 'bg-purple-100 text-purple-700', website: 'bg-blue-100 text-blue-700', both: 'bg-green-100 text-green-700' };
                          const labels = { app: 'APP', website: 'WEB', both: 'BOTH' };
                          return <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${styles[p]}`} data-testid={`coupon-platform-${c.id}`}>{labels[p]}</span>;
                        })()}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {c.discountType === 'percentage' ? `${c.discountValue}% off` : `₹${c.discountValue} off`}
                        {c.minOrderAmount > 0 && ` | Min ₹${c.minOrderAmount}`}
                        {c.maxUses > 0 && ` | ${c.usedCount}/${c.maxUses} used`}
                        {c.expiresAt && ` | Expires ${new Date(c.expiresAt).toLocaleDateString()}`}
                      </p>
                    </div>
                    <Button onClick={() => handleDeleteCoupon(c.id)} className="bg-red-500 text-white hover:bg-red-600 px-3 py-2" data-testid={`delete-coupon-${c.id}`}>
                      <Trash2 size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* INVENTORY TAB */}
          <TabsContent value="inventory">
            <h2 className="text-2xl font-bold mb-4">Inventory Management</h2>
            {inventoryAlerts.length > 0 && (
              <div className="bg-red-50 border border-red-200 p-3 rounded-lg mb-4 flex items-center gap-2">
                <AlertTriangle size={18} className="text-red-500" />
                <span className="text-sm text-red-700 font-medium">{inventoryAlerts.length} product(s) below stock threshold</span>
              </div>
            )}
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 text-xs font-medium">Product</th>
                    <th className="text-center p-3 text-xs font-medium">Stock</th>
                    <th className="text-center p-3 text-xs font-medium">Sold</th>
                    <th className="text-center p-3 text-xs font-medium">Low Threshold</th>
                    <th className="text-center p-3 text-xs font-medium">Status</th>
                    <th className="text-center p-3 text-xs font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => {
                    const threshold = product.lowStockThreshold || 10;
                    const isLow = product.stock <= threshold;
                    const isOut = product.stock <= 0;
                    return (
                      <tr key={product.id} className={`border-b ${isOut ? 'bg-red-50' : isLow ? 'bg-orange-50' : ''}`} data-testid={`inv-row-${product.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <img src={product.image} alt="" className="w-8 h-8 rounded object-cover" />
                            <span className="font-medium text-sm">{product.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <input type="number" defaultValue={product.stock} min="0"
                            className={`w-20 text-center border rounded px-2 py-1 text-sm font-bold ${isOut ? 'border-red-300 text-red-600' : isLow ? 'border-orange-300 text-orange-600' : 'border-gray-300'}`}
                            onBlur={async (e) => {
                              const val = parseInt(e.target.value);
                              if (isNaN(val) || val === product.stock) return;
                              try { await axios.put(`${API}/products/${product.id}`, { stock: val }, axiosConfig); toast.success('Stock updated'); fetchAllData(); } catch { toast.error('Failed'); }
                            }}
                            data-testid={`inv-stock-${product.id}`} />
                        </td>
                        <td className="p-3 text-center">
                          <input type="number" defaultValue={product.soldCount || 0} min="0"
                            className="w-20 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                            onBlur={async (e) => {
                              const val = parseInt(e.target.value);
                              if (isNaN(val) || val === (product.soldCount || 0)) return;
                              try { await axios.put(`${API}/products/${product.id}`, { soldCount: val }, axiosConfig); toast.success('Sold count updated'); fetchAllData(); } catch { toast.error('Failed'); }
                            }}
                            data-testid={`inv-sold-${product.id}`} />
                        </td>
                        <td className="p-3 text-center">
                          <input type="number" defaultValue={threshold} min="0"
                            className="w-20 text-center border border-gray-300 rounded px-2 py-1 text-sm"
                            onBlur={async (e) => {
                              const val = parseInt(e.target.value);
                              if (isNaN(val) || val === threshold) return;
                              try { await axios.put(`${API}/products/${product.id}`, { lowStockThreshold: val }, axiosConfig); toast.success('Threshold updated'); fetchAllData(); } catch { toast.error('Failed'); }
                            }}
                            data-testid={`inv-threshold-${product.id}`} />
                        </td>
                        <td className="p-3 text-center">
                          <span className={`text-[10px] px-2 py-1 rounded font-medium ${isOut ? 'bg-red-100 text-red-700' : isLow ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                            {isOut ? 'OUT OF STOCK' : isLow ? 'LOW STOCK' : 'IN STOCK'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-xs text-gray-400">Price: ₹{product.price}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users">
            <h2 className="text-2xl font-bold mb-6">Users ({users.length})</h2>
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-4 text-sm font-medium">Name</th>
                    <th className="text-left p-4 text-sm font-medium">Email</th>
                    <th className="text-left p-4 text-sm font-medium">Provider</th>
                    <th className="text-left p-4 text-sm font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.uid} className="border-b" data-testid={`admin-user-${user.uid}`}>
                      <td className="p-4">{user.name}</td>
                      <td className="p-4">{user.email}</td>
                      <td className="p-4">{user.provider}</td>
                      <td className="p-4">{new Date(user.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* BULK REQUESTS TAB */}
          <TabsContent value="bulk">
            <h2 className="text-2xl font-bold mb-6">Bulk Requests ({bulkRequests.length})</h2>
            {bulkRequests.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No bulk requests yet</div>
            ) : (
              <div className="space-y-4">
                {bulkRequests.map(req => (
                  <div key={req.id} className="card p-6" data-testid={`bulk-request-${req.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-bold">{req.name} {req.company && <span className="text-gray-500 font-normal">({req.company})</span>}</h3>
                        <p className="text-sm text-gray-600">{req.email} | {req.phone}</p>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(req.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="mt-3 text-gray-700 bg-gray-50 p-3">{req.message}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* REVIEWS TAB */}
          <TabsContent value="reviews">
            <h2 className="text-2xl font-bold mb-6">Customer Reviews ({reviews.length})</h2>
            {reviews.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No reviews yet</div>
            ) : (
              <div className="space-y-4">
                {reviews.map(review => {
                  const product = products.find(p => p.id === review.productId);
                  const isEditing = editingReview === review.id;
                  return (
                    <div key={review.id} className="card p-5" data-testid={`admin-review-${review.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1 flex-wrap">
                            {isEditing ? (
                              <input defaultValue={review.userName} className="font-bold border-b border-[#D4AF37] bg-transparent outline-none px-1" data-testid={`edit-review-name-${review.id}`}
                                onBlur={(e) => handleUpdateReview(review.id, { userName: e.target.value })} />
                            ) : (
                              <span className="font-bold">{review.userName}</span>
                            )}
                            <span className="text-xs text-gray-400">{review.userEmail}</span>
                            {review.verified && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Verified Purchase</span>}
                            <div className="flex gap-0.5">
                              {[1,2,3,4,5].map(s => (
                                <Star key={s} size={14}
                                  className={`${s <= review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'} ${isEditing ? 'cursor-pointer' : ''}`}
                                  onClick={isEditing ? () => handleUpdateReview(review.id, { rating: s }) : undefined}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-gray-500 mb-1">
                            Product: <span className="font-medium text-[#0A192F]">{product?.name || review.productId}</span>
                          </p>
                          {isEditing ? (
                            <textarea defaultValue={review.comment} rows={2} className="w-full border border-gray-300 px-2 py-1 text-sm mt-1 rounded"
                              onBlur={(e) => handleUpdateReview(review.id, { comment: e.target.value })} data-testid={`edit-review-comment-${review.id}`} />
                          ) : (
                            <>
                              {review.title && <p className="font-medium text-sm">{review.title}</p>}
                              <p className="text-gray-600 text-sm mt-1">{review.comment}</p>
                            </>
                          )}
                          <p className="text-[10px] text-gray-400 mt-2">{new Date(review.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button onClick={() => setEditingReview(isEditing ? null : review.id)} variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white px-3 py-2" data-testid={`edit-review-btn-${review.id}`}>
                            <Edit size={16} />
                          </Button>
                          <Button onClick={() => handleDeleteReview(review.id)} className="bg-red-500 text-white hover:bg-red-600 px-3 py-2" data-testid={`delete-review-${review.id}`}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Sale Notifications ({notifications.length})</h2>
              {notifications.some(n => !n.read) && (
                <Button onClick={handleMarkAllNotificationsRead} variant="outline" className="border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white" data-testid="mark-all-read-btn">Mark All Read</Button>
              )}
            </div>
            {notifications.length === 0 ? (
              <div className="card p-12 text-center text-gray-500">No notifications yet. Notifications appear when orders are placed.</div>
            ) : (
              <div className="space-y-3">
                {notifications.map(notif => (
                  <div key={notif.id} className={`card p-4 ${!notif.read ? 'border-l-4 border-[#D4AF37]' : ''}`} data-testid={`notification-${notif.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Bell size={14} className={!notif.read ? 'text-[#D4AF37]' : 'text-gray-400'} />
                          <span className="font-bold text-sm">{notif.orderId}</span>
                          {!notif.read && <span className="text-[10px] bg-[#D4AF37] text-white px-1.5 py-0.5 rounded">New</span>}
                        </div>
                        <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">{notif.message}</pre>
                        <p className="text-[10px] text-gray-400 mt-2">{new Date(notif.createdAt).toLocaleString()}</p>
                      </div>
                      {notif.whatsappUrl && (
                        <a href={notif.whatsappUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 bg-green-500 text-white px-3 py-2 text-xs font-medium rounded hover:bg-green-600 flex-shrink-0"
                          data-testid={`whatsapp-link-${notif.id}`}>
                          <ExternalLink size={14} /> WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold">Global Settings</h2>
              <div className={`text-xs font-bold px-3 py-2 rounded-lg ${
                adminPlatform === 'all' ? 'bg-gray-100 text-gray-800' :
                adminPlatform === 'app' ? 'bg-purple-100 text-purple-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {adminPlatform === 'all' && '🌐 Editing SHARED settings (affects both)'}
                {adminPlatform === 'app' && '📱 Editing APP-ONLY — Website stays unchanged'}
                {adminPlatform === 'website' && '💻 Editing WEBSITE-ONLY — App stays unchanged'}
              </div>
            </div>
            {settings && (
              <div className="card p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>GST (%)</Label>
                    <Input type="number" step="0.01" value={settings.gst} onChange={(e) => setSettings({...settings, gst: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="settings-gst-input" />
                  </div>
                  <div>
                    <Label>Shipping Charge (₹)</Label>
                    <Input type="number" step="0.01" value={settings.shipping} onChange={(e) => setSettings({...settings, shipping: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="settings-shipping-input" />
                  </div>
                  <div>
                    <Label>Free Shipping Threshold (₹)</Label>
                    <Input type="number" step="0.01" value={settings.freeShippingThreshold} onChange={(e) => setSettings({...settings, freeShippingThreshold: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="settings-free-shipping-input" />
                  </div>
                  <div>
                    <Label>Min COD Order Amount (₹)</Label>
                    <Input type="number" step="0.01" value={settings.minCodAmount || 0} onChange={(e) => setSettings({...settings, minCodAmount: parseFloat(e.target.value) || 0})} className="mt-1" placeholder="0 = no minimum" data-testid="settings-min-cod-input" />
                  </div>
                  <div>
                    <Label>WhatsApp Number</Label>
                    <Input value={settings.whatsappNumber} onChange={(e) => setSettings({...settings, whatsappNumber: e.target.value})} className="mt-1" data-testid="settings-whatsapp-input" />
                  </div>
                  <div>
                    <Label>Extra Charges (₹)</Label>
                    <Input type="number" step="0.01" value={settings.extraCharges || 0} onChange={(e) => setSettings({...settings, extraCharges: parseFloat(e.target.value) || 0})} className="mt-1" placeholder="0 = none" data-testid="settings-extra-charges-input" />
                  </div>
                  <div>
                    <Label>Extra Charges Label</Label>
                    <Input value={settings.extraChargesLabel || 'Other Charges'} onChange={(e) => setSettings({...settings, extraChargesLabel: e.target.value})} className="mt-1" placeholder="e.g. Handling Fee, Packaging" data-testid="settings-extra-charges-label" />
                  </div>
                  <div>
                    <Label>Discount (%)</Label>
                    <Input type="number" step="0.01" value={settings.discount} onChange={(e) => setSettings({...settings, discount: parseFloat(e.target.value) || 0})} className="mt-1" data-testid="settings-discount-input" />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <Input value={settings.currency} onChange={(e) => setSettings({...settings, currency: e.target.value})} className="mt-1" data-testid="settings-currency-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Banner Text</Label>
                    <Input value={settings.bannerText} onChange={(e) => setSettings({...settings, bannerText: e.target.value})} className="mt-1" data-testid="settings-banner-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Contact Email</Label>
                    <Input value={settings.contactEmail || ''} onChange={(e) => setSettings({...settings, contactEmail: e.target.value})} className="mt-1" data-testid="settings-email-input" />
                  </div>
                  <div>
                    <Label>Instagram URL</Label>
                    <Input value={settings.instagramUrl || ''} onChange={(e) => setSettings({...settings, instagramUrl: e.target.value})} className="mt-1" placeholder="https://instagram.com/..." data-testid="settings-instagram-input" />
                  </div>
                  <div>
                    <Label>Facebook URL</Label>
                    <Input value={settings.facebookUrl || ''} onChange={(e) => setSettings({...settings, facebookUrl: e.target.value})} className="mt-1" placeholder="https://facebook.com/..." data-testid="settings-facebook-input" />
                  </div>
                  <div>
                    <Label>Twitter URL</Label>
                    <Input value={settings.twitterUrl || ''} onChange={(e) => setSettings({...settings, twitterUrl: e.target.value})} className="mt-1" placeholder="https://twitter.com/..." data-testid="settings-twitter-input" />
                  </div>
                  <div>
                    <Label>YouTube URL</Label>
                    <Input value={settings.youtubeUrl || ''} onChange={(e) => setSettings({...settings, youtubeUrl: e.target.value})} className="mt-1" placeholder="https://youtube.com/..." data-testid="settings-youtube-input" />
                  </div>
                  <div>
                    <Label>Amazon Store URL</Label>
                    <Input value={settings.amazonUrl || ''} onChange={(e) => setSettings({...settings, amazonUrl: e.target.value})} className="mt-1" placeholder="https://amazon.in/..." data-testid="settings-amazon-input" />
                  </div>
                  <div>
                    <Label>Flipkart Store URL</Label>
                    <Input value={settings.flipkartUrl || ''} onChange={(e) => setSettings({...settings, flipkartUrl: e.target.value})} className="mt-1" placeholder="https://flipkart.com/..." data-testid="settings-flipkart-input" />
                  </div>
                  <div>
                    <Label>Meesho Store URL</Label>
                    <Input value={settings.meeshoUrl || ''} onChange={(e) => setSettings({...settings, meeshoUrl: e.target.value})} className="mt-1" placeholder="https://meesho.com/..." data-testid="settings-meesho-input" />
                  </div>
                  <div>
                    <Label>IndiaMART URL</Label>
                    <Input value={settings.indiamartUrl || ''} onChange={(e) => setSettings({...settings, indiamartUrl: e.target.value})} className="mt-1" placeholder="https://indiamart.com/..." data-testid="settings-indiamart-input" />
                  </div>
                </div>
                <Button onClick={handleSaveSettings} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] mt-6" data-testid="save-settings-btn">
                  <Save className="mr-2" size={18} /> Save Settings
                </Button>
              </div>
            )}

            {/* Category Management */}
            <div className="card p-6 mt-6">
              <h3 className="text-lg font-bold mb-4">Manage Categories</h3>
              <div className="space-y-2 mb-4">
                {categories.map((cat, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded" data-testid={`category-${idx}`}>
                    <input defaultValue={cat} className="bg-transparent font-medium text-sm flex-1 outline-none"
                      onBlur={async (e) => {
                        const newName = e.target.value.trim();
                        if (!newName || newName === cat) return;
                        const updated = categories.map((c, i) => i === idx ? newName : c);
                        try { await axios.put(`${API}/categories`, { categories: updated }, axiosConfig); toast.success('Category renamed'); fetchAllData(); } catch (err) { toast.error('Failed'); }
                      }} />
                    <button onClick={async () => {
                      if (!window.confirm(`Delete "${cat}"?`)) return;
                      try { await axios.delete(`${API}/categories/${encodeURIComponent(cat)}`, axiosConfig); toast.success('Category deleted'); fetchAllData(); } catch { toast.error('Failed'); }
                    }} className="text-red-400 hover:text-red-600 ml-2"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category name" className="flex-1 h-9" data-testid="new-category-input" />
                <Button onClick={async () => {
                  if (!newCategory.trim()) return;
                  try { await axios.post(`${API}/categories`, { name: newCategory.trim() }, axiosConfig); toast.success('Category added'); setNewCategory(''); fetchAllData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                }} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] h-9 px-4 text-xs" data-testid="add-category-btn">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="ui">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-2xl font-bold">UI Controls</h2>
              <div className={`text-xs font-bold px-3 py-2 rounded-lg ${
                adminPlatform === 'all' ? 'bg-gray-100 text-gray-800' :
                adminPlatform === 'app' ? 'bg-purple-100 text-purple-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {adminPlatform === 'all' && '🌐 Editing SHARED UI (affects both Website & App)'}
                {adminPlatform === 'app' && '📱 Editing APP-ONLY UI — Website stays unchanged'}
                {adminPlatform === 'website' && '💻 Editing WEBSITE-ONLY UI — App stays unchanged'}
              </div>
            </div>
            {uiControls && (
              <div className="space-y-6">
                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">WhatsApp Button</h3>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="whatsapp-enabled" checked={uiControls.whatsappEnabled}
                      onChange={(e) => setUIControls({...uiControls, whatsappEnabled: e.target.checked})}
                      className="w-4 h-4" data-testid="ui-whatsapp-enabled" />
                    <Label htmlFor="whatsapp-enabled">Enable WhatsApp Button</Label>
                  </div>
                </div>

                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">Live Sale Popup</h3>
                  <div className="flex items-center gap-3 mb-4">
                    <input type="checkbox" id="popup-enabled" checked={uiControls.liveSalePopupEnabled}
                      onChange={(e) => setUIControls({...uiControls, liveSalePopupEnabled: e.target.checked})}
                      className="w-4 h-4" data-testid="ui-popup-enabled" />
                    <Label htmlFor="popup-enabled">Enable Live Sale Popup</Label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Popup Message</Label>
                      <Input value={uiControls.liveSalePopupMessage}
                        onChange={(e) => setUIControls({...uiControls, liveSalePopupMessage: e.target.value})}
                        className="mt-1" data-testid="ui-popup-message" />
                      <p className="text-xs text-gray-500 mt-1">Use {'{product}'} and {'{city}'} as placeholders</p>
                    </div>
                    <div>
                      <Label>Popup Interval (seconds)</Label>
                      <Input type="number" min="5" max="300" step="1" value={Math.round(Math.min(300000, uiControls.liveSalePopupFrequency || 30000) / 1000)}
                        onChange={(e) => { const v = Math.min(300, Math.max(5, parseInt(e.target.value) || 30)); setUIControls({...uiControls, liveSalePopupFrequency: v * 1000}); }}
                        className="mt-1" data-testid="ui-popup-frequency" />
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">Hero Section</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Hero Title</Label>
                        <Input value={uiControls.heroText} onChange={(e) => setUIControls({...uiControls, heroText: e.target.value})} className="mt-1" data-testid="ui-hero-text" />
                      </div>
                      <div>
                        <Label>Hero Subtitle</Label>
                        <Input value={uiControls.heroSubtext} onChange={(e) => setUIControls({...uiControls, heroSubtext: e.target.value})} className="mt-1" data-testid="ui-hero-subtext" />
                      </div>
                    </div>
                    <div>
                      <Label>Hero Background Image</Label>
                      <div className="mt-1">
                        <ImageUploader value={uiControls.heroImage || ''} onChange={(url) => setUIControls({...uiControls, heroImage: url})} testId="ui-hero-image" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">Category Images</h3>
                  <p className="text-sm text-gray-500 mb-4">Override default category images. Leave blank to use defaults.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Festival Lighting</Label>
                      <div className="mt-1"><ImageUploader value={uiControls.categoryFestivalImage || ''} onChange={(url) => setUIControls({...uiControls, categoryFestivalImage: url})} testId="ui-cat-festival" /></div>
                    </div>
                    <div>
                      <Label>Decorative Lighting</Label>
                      <div className="mt-1"><ImageUploader value={uiControls.categoryDecorativeImage || ''} onChange={(url) => setUIControls({...uiControls, categoryDecorativeImage: url})} testId="ui-cat-decorative" /></div>
                    </div>
                    <div>
                      <Label>Industrial Lighting</Label>
                      <div className="mt-1"><ImageUploader value={uiControls.categoryIndustrialImage || ''} onChange={(url) => setUIControls({...uiControls, categoryIndustrialImage: url})} testId="ui-cat-industrial" /></div>
                    </div>
                    <div>
                      <Label>LED Panels</Label>
                      <div className="mt-1"><ImageUploader value={uiControls.categoryLedImage || ''} onChange={(url) => setUIControls({...uiControls, categoryLedImage: url})} testId="ui-cat-led" /></div>
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">Feature Highlights</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>Feature 1 Title</Label><Input value={uiControls.featureText1 || ''} onChange={(e) => setUIControls({...uiControls, featureText1: e.target.value})} className="mt-1" data-testid="ui-feat1-title" /></div>
                    <div><Label>Feature 1 Description</Label><Input value={uiControls.featureDesc1 || ''} onChange={(e) => setUIControls({...uiControls, featureDesc1: e.target.value})} className="mt-1" data-testid="ui-feat1-desc" /></div>
                    <div><Label>Feature 2 Title</Label><Input value={uiControls.featureText2 || ''} onChange={(e) => setUIControls({...uiControls, featureText2: e.target.value})} className="mt-1" data-testid="ui-feat2-title" /></div>
                    <div><Label>Feature 2 Description</Label><Input value={uiControls.featureDesc2 || ''} onChange={(e) => setUIControls({...uiControls, featureDesc2: e.target.value})} className="mt-1" data-testid="ui-feat2-desc" /></div>
                    <div><Label>Feature 3 Title</Label><Input value={uiControls.featureText3 || ''} onChange={(e) => setUIControls({...uiControls, featureText3: e.target.value})} className="mt-1" data-testid="ui-feat3-title" /></div>
                    <div><Label>Feature 3 Description</Label><Input value={uiControls.featureDesc3 || ''} onChange={(e) => setUIControls({...uiControls, featureDesc3: e.target.value})} className="mt-1" data-testid="ui-feat3-desc" /></div>
                  </div>
                </div>

                <div className="card p-6">
                  <h3 className="font-bold text-lg mb-4">CTA Section (Bottom Banner)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><Label>CTA Title</Label><Input value={uiControls.ctaTitle || ''} onChange={(e) => setUIControls({...uiControls, ctaTitle: e.target.value})} className="mt-1" data-testid="ui-cta-title" /></div>
                    <div><Label>CTA Subtitle</Label><Input value={uiControls.ctaSubtext || ''} onChange={(e) => setUIControls({...uiControls, ctaSubtext: e.target.value})} className="mt-1" data-testid="ui-cta-subtext" /></div>
                  </div>
                </div>

                <Button onClick={handleSaveUIControls} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="save-ui-btn">
                  <Save className="mr-2" size={18} /> Save UI Controls
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ============ PUSH NOTIFICATIONS TAB (App) ============ */}
          <TabsContent value="push">
            <div className="mb-6 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#c9a22e] flex items-center justify-center text-white text-lg">📱</div>
              <div>
                <h2 className="text-2xl font-bold">Push Notifications</h2>
                <p className="text-sm text-gray-600">Send messages directly to your mobile app users.</p>
              </div>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="card p-4">
                <div className="text-xs text-gray-500 font-medium">Total tokens</div>
                <div className="text-2xl font-bold text-[#0A192F] mt-1" data-testid="push-total-tokens">{pushTokens.length}</div>
              </div>
              <div className="card p-4">
                <div className="text-xs text-gray-500 font-medium">Active devices</div>
                <div className="text-2xl font-bold text-green-600 mt-1">{pushTokens.filter(t => t.active).length}</div>
              </div>
              <div className="card p-4">
                <div className="text-xs text-gray-500 font-medium">Logged-in users</div>
                <div className="text-2xl font-bold text-[#D4AF37] mt-1">{pushTokens.filter(t => t.userId).length}</div>
              </div>
              <div className="card p-4">
                <div className="text-xs text-gray-500 font-medium">Notifications sent</div>
                <div className="text-2xl font-bold text-blue-600 mt-1">{notifHistory.length}</div>
              </div>
            </div>

            {/* Auto-notification toggles */}
            <div className="card p-5 mb-6" data-testid="auto-notif-card">
              <h3 className="font-bold mb-3 flex items-center gap-2">⚡ Auto Order Notifications</h3>
              <p className="text-xs text-gray-600 mb-4">Automatically notify app users when their order status changes.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { key: 'placed', label: '🎉 Order placed', desc: 'Sent when order is first placed' },
                  { key: 'confirmed', label: '✅ Confirmed', desc: 'Sent when payment is confirmed' },
                  { key: 'shipped', label: '🚚 Shipped', desc: 'Sent when order is shipped' },
                  { key: 'delivered', label: '✨ Delivered', desc: 'Sent when order is delivered' },
                ].map(t => (
                  <label key={t.key} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:border-[#D4AF37] cursor-pointer" data-testid={`auto-notif-${t.key}`}>
                    <input
                      type="checkbox"
                      checked={!!autoNotifSettings[t.key]}
                      onChange={() => handleAutoNotifToggle(t.key)}
                      className="mt-1 w-4 h-4 text-[#D4AF37]"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{t.label}</div>
                      <div className="text-xs text-gray-500">{t.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Send form */}
            <form onSubmit={handleSendPush} className="card p-6 mb-6" data-testid="push-send-form">
              <h3 className="font-bold mb-4">Send new notification</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Title * <span className="text-gray-400 text-xs font-normal">({pushForm.title.length}/100)</span></Label>
                  <Input maxLength={100} value={pushForm.title}
                    onChange={(e) => setPushForm({ ...pushForm, title: e.target.value })}
                    placeholder="e.g. 🎉 Diwali Sale is LIVE!"
                    data-testid="push-title-input" />
                </div>
                <div className="md:col-span-2">
                  <Label>Body * <span className="text-gray-400 text-xs font-normal">({pushForm.body.length}/240)</span></Label>
                  <textarea maxLength={240} value={pushForm.body}
                    onChange={(e) => setPushForm({ ...pushForm, body: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    rows={3}
                    placeholder="Up to 40% off on all lighting. Shop now before sale ends."
                    data-testid="push-body-input" />
                </div>
                <div>
                  <Label>Image URL (optional)</Label>
                  <Input value={pushForm.imageUrl}
                    onChange={(e) => setPushForm({ ...pushForm, imageUrl: e.target.value })}
                    placeholder="/api/images/…"
                    data-testid="push-image-input" />
                </div>
                <div>
                  <Label>Audience</Label>
                  <select value={pushForm.audience}
                    onChange={(e) => setPushForm({ ...pushForm, audience: e.target.value })}
                    className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm"
                    data-testid="push-audience-select">
                    <option value="all">Everyone</option>
                    <option value="logged_in">Logged-in users only</option>
                  </select>
                </div>
                <div>
                  <Label>Deep-link target</Label>
                  <select value={pushForm.deepLinkType}
                    onChange={(e) => setPushForm({ ...pushForm, deepLinkType: e.target.value, deepLinkTarget: '' })}
                    className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm"
                    data-testid="push-deeplink-type">
                    <option value="home">Home screen</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                    <option value="order">Order</option>
                    <option value="bulk">Bulk Inquiry</option>
                  </select>
                </div>
                <div>
                  <Label>Target ID / name</Label>
                  {pushForm.deepLinkType === 'product' ? (
                    <select value={pushForm.deepLinkTarget}
                      onChange={(e) => setPushForm({ ...pushForm, deepLinkTarget: e.target.value })}
                      className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm">
                      <option value="">— Select product —</option>
                      {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : pushForm.deepLinkType === 'category' ? (
                    <select value={pushForm.deepLinkTarget}
                      onChange={(e) => setPushForm({ ...pushForm, deepLinkTarget: e.target.value })}
                      className="w-full h-10 px-3 py-2 border border-gray-300 bg-white text-sm">
                      <option value="">— Select category —</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <Input value={pushForm.deepLinkTarget}
                      onChange={(e) => setPushForm({ ...pushForm, deepLinkTarget: e.target.value })}
                      placeholder={pushForm.deepLinkType === 'home' ? '(no target needed)' : 'ID or leave blank'} />
                  )}
                </div>
                <div>
                  <Label>Schedule (optional)</Label>
                  <Input type="datetime-local" value={pushForm.scheduledFor}
                    onChange={(e) => setPushForm({ ...pushForm, scheduledFor: e.target.value })}
                    data-testid="push-schedule-input" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <Button type="submit" className="bg-gradient-to-r from-[#D4AF37] to-[#c9a22e] text-white hover:opacity-90" disabled={sendingPush} data-testid="push-send-btn">
                  {sendingPush ? 'Sending…' : (pushForm.scheduledFor ? '⏰ Schedule' : '📤 Send Now')}
                </Button>
                <Button type="button" variant="outline" onClick={() => setPushForm({ title: '', body: '', imageUrl: '', deepLinkType: 'home', deepLinkTarget: '', audience: 'all', scheduledFor: '' })}>Clear</Button>
              </div>
            </form>

            {/* History */}
            <div className="card p-5">
              <h3 className="font-bold mb-3">Notification history</h3>
              {notifHistory.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No notifications sent yet.</p>
              ) : (
                <div className="space-y-3">
                  {notifHistory.map(n => (
                    <div key={n.id} className="p-3 border border-gray-200 rounded-lg flex items-center gap-3" data-testid={`notif-${n.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-sm truncate">{n.title}</div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                            n.status === 'sent' ? 'bg-green-100 text-green-700' :
                            n.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                            n.status === 'failed' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-600'
                          }`}>{(n.status || 'sent').toUpperCase()}</span>
                          {n.type === 'order_auto' && <span className="text-[10px] px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-700">AUTO</span>}
                        </div>
                        <div className="text-xs text-gray-600 mt-1 line-clamp-2">{n.body}</div>
                        <div className="text-[11px] text-gray-400 mt-1">
                          {new Date(n.createdAt).toLocaleString()} · {n.recipientCount || 0} recipient(s)
                          {n.scheduledFor && ` · scheduled for ${new Date(n.scheduledFor).toLocaleString()}`}
                        </div>
                      </div>
                      {n.type !== 'order_auto' && n.status === 'sent' && (
                        <Button size="sm" variant="outline" onClick={() => handleResendPush(n)} data-testid={`resend-${n.id}`}>Resend</Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ============ PLATFORM-SPECIFIC SETTINGS (App vs Website) ============ */}
          <TabsContent value="platform-settings">
            <div className="mb-6 flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0A192F] flex items-center justify-center text-white">⚙</div>
              <div>
                <h2 className="text-2xl font-bold">App vs Website</h2>
                <p className="text-sm text-gray-600">Override shared settings just for the app or just for the website. Empty fields → use shared value.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* App overrides */}
              <div className="card p-5 border-l-4 border-l-purple-500">
                <h3 className="font-bold mb-4 flex items-center gap-2">📱 App-only Overrides</h3>
                {['gst', 'shipping', 'minCodAmount', 'bannerText'].map(key => (
                  <div key={key} className="mb-3">
                    <Label className="capitalize">{key}</Label>
                    <Input
                      type={['gst', 'shipping', 'minCodAmount'].includes(key) ? 'number' : 'text'}
                      value={appSettingsOverride[key] ?? ''}
                      onChange={(e) => setAppSettingsOverride({ ...appSettingsOverride, [key]: e.target.value === '' ? undefined : (['gst', 'shipping', 'minCodAmount'].includes(key) ? parseFloat(e.target.value) : e.target.value) })}
                      placeholder={`Shared: ${settings?.[key] ?? '—'}`}
                      data-testid={`app-override-${key}`}
                    />
                  </div>
                ))}
                <Button onClick={() => handleSavePlatformSettings('app')} className="bg-purple-600 text-white hover:bg-purple-700 mt-2" data-testid="save-app-settings">Save App Overrides</Button>
              </div>

              {/* Website overrides */}
              <div className="card p-5 border-l-4 border-l-blue-500">
                <h3 className="font-bold mb-4 flex items-center gap-2">🌐 Website-only Overrides</h3>
                {['gst', 'shipping', 'minCodAmount', 'bannerText'].map(key => (
                  <div key={key} className="mb-3">
                    <Label className="capitalize">{key}</Label>
                    <Input
                      type={['gst', 'shipping', 'minCodAmount'].includes(key) ? 'number' : 'text'}
                      value={webSettingsOverride[key] ?? ''}
                      onChange={(e) => setWebSettingsOverride({ ...webSettingsOverride, [key]: e.target.value === '' ? undefined : (['gst', 'shipping', 'minCodAmount'].includes(key) ? parseFloat(e.target.value) : e.target.value) })}
                      placeholder={`Shared: ${settings?.[key] ?? '—'}`}
                      data-testid={`web-override-${key}`}
                    />
                  </div>
                ))}
                <Button onClick={() => handleSavePlatformSettings('website')} className="bg-blue-600 text-white hover:bg-blue-700 mt-2" data-testid="save-web-settings">Save Website Overrides</Button>
              </div>
            </div>

            <div className="mt-5 p-4 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
              💡 <b>How this works:</b> An empty field means the platform uses the shared setting. Fill only the ones you want different per platform. Settings in the main <b>Settings</b> tab continue to be the shared base for both.
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
